import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import qs from 'qs';
import { parseISO, format, isToday, isValid } from 'date-fns';
import { motion } from 'framer-motion';

import { API_URL, get } from 'lib/network/api';
import FullLayout from 'layout/FullLayout';
import { sortByStartTime } from 'lib/sortByStartTime';
import { AgeDefault, TypeClassDefault } from 'lib/DefaultData';

import AutocompleteDropdown from 'components/AutocompleteDropdown';
import SliderDate from 'components/SliderDate';
import InputSelected from 'components/InputSelected';
import ModalBooking from './ComponentsPage/ModalBooking';
import CardClassSchedule from './ComponentsPage/CardClassSchedule';

const ClassSchedule = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentParams = qs.parse(searchParams.toString(), { comma: true });

  const [openBooking, setOpenBooking] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);

  useEffect(() => {
    if (!currentParams.date) {
      const today = format(new Date(), 'yyyy-MM-dd');
      setSearchParams({ ...currentParams, date: today });
    }
  }, [currentParams.date, setSearchParams]);

  const currentDate =
    currentParams.date && isValid(parseISO(currentParams.date))
      ? parseISO(currentParams.date)
      : new Date();

  const formatPickDate = (date) => {
    const labelToday = isToday(date) ? 'Today, ' : '';
    return `${labelToday}${format(date, 'EEEE d MMMM')}`;
  };

  const fetchClassSchedule = async () => {
    const filteredParams = Object.fromEntries(
      Object.entries({
        page: currentParams.page || 1,
        limit: currentParams.limit || 50,
        data_type: 'class_schedule',
        date: currentParams.date,
        age_group: currentParams.age_group,
        class_type: currentParams.class_type,
        teacher_id: currentParams.teacher_id,
        class_id: currentParams.class_id,
      }).filter(([, value]) => value !== '' && value !== undefined),
    );

    return get(
      `${API_URL}/api/event/schedule/no-login?${qs.stringify(filteredParams, {
        arrayFormat: 'repeat',
      })}`,
    );
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['fetchClassSchedule', currentParams],
    queryFn: fetchClassSchedule,
    staleTime: 1000 * 60 * 3,
    keepPreviousData: true,
  });

  const classPackage = data?.class_schedules || [];

  const fetchListTeacher = async ({ pageParam = 1 }) => {
    const params = {
      page: pageParam,
      limit: 10,
    };
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
    return get(
      `${API_URL}/api/teachers/no-login?${qs.stringify(filteredParams, {
        arrayFormat: 'repeat',
      })}`,
    );
  };

  const getTeacherOptions = (data) => {
    if (!data || !Array.isArray(data)) return [];
    const options = data?.map((item) => ({
      id: item.id,
      name: item.name,
      value: item.id,
    }));
    return options;
  };

  const {
    data: teacherData,
    isLoading: isLoadingTeachers,
    hasNextPage: hasMoreTeachers,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['fetchListTeacher'],
    queryFn: fetchListTeacher,
    retry: 2,
    getNextPageParam: (lastPage, allPages) => {
      const teachers = lastPage?.data?.teachers || [];
      return teachers.length === 10 ? allPages.length + 1 : undefined;
    },
  });

  const teacherOptions = teacherData
    ? teacherData.pages.flatMap((page) => getTeacherOptions(page?.data?.teachers))
    : [];

  const handleLoadMoreTeachers = () => {
    if (hasMoreTeachers && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    updateParam('teacher_id', teacher?.value || '');
  };

  const updateParam = (key, value) => {
    const updated = { ...currentParams };

    if (!value) delete updated[key];
    else updated[key] = value;

    setSearchParams(updated);
  };

  const onFilterByDate = (date) => {
    updateParam('date', format(date, 'yyyy-MM-dd'));
  };

  const onBooking = (scheduleItem) => {
    setOpenBooking(true);
    setSelectedSchedule(scheduleItem);
  };

  const SkeletonCard = () => (
    <div className="animate-pulse rounded-xl border p-4 bg-white">
      <div className="h-5 w-1/3 bg-gray-200 mb-2 rounded"></div>
      <div className="h-4 w-1/2 bg-gray-200 mb-3 rounded"></div>
      <div className="h-3 w-full bg-gray-200 rounded"></div>
      <div className="h-3 w-3/5 bg-gray-200 mt-2 rounded"></div>
    </div>
  );

  return (
    <FullLayout>
      <div className="relative">
        {/* Header Filter */}
        <div className="bg-kin-textsecondary shadow-md xl:pb-6 xl:px-0 pt-2 pb-2 px-4 lg:px-8 sticky top-[60px] z-10">
          <div className="container mx-auto">
            <h2 className="text-2xl text-kin-text font-semibold">Our Class Schedule</h2>

            {/* Slider */}
            <SliderDate pickDate={currentDate} setPickDate={onFilterByDate} />

            {/* Filter */}
            <div className="hidden xl:flex flex-row justify-end gap-2 items-center">
              <InputSelected
                dataOption={TypeClassDefault}
                placeholder="Type Class"
                selected={
                  TypeClassDefault.find((i) => i.value === currentParams.class_type) || null
                }
                setSelected={(opt) => updateParam('class_type', opt?.value || '')}
                showClearOption
                onClear={() => updateParam('class_type', '')}
              />

              <InputSelected
                dataOption={AgeDefault.map((i) => ({
                  name: `${i.name} ${i.description}`,
                  value: i.value,
                }))}
                placeholder="Age Group"
                selected={AgeDefault.find((i) => i.value === currentParams.age_group) || null}
                setSelected={(opt) => updateParam('age_group', opt?.value || '')}
                showClearOption
                onClear={() => updateParam('age_group', '')}
              />

              <AutocompleteDropdown
                options={teacherOptions}
                selected={selectedTeacher}
                setSelected={handleTeacherSelect}
                onLoadMore={handleLoadMoreTeachers}
                isLoadingMore={isFetchingNextPage}
                hasMore={hasMoreTeachers}
                placeholder="Search Teachers..."
                onClear={() => updateParam('teacher_id', '')}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto">
          <div className="flex flex-col mt-0 md:mt-6 pb-4 gap-4">
            <div className="p-6 relative overflow-hidden">
              <div className="flex flex-col items-start md:flex-row md:items-center justify-between mb-4">
                <span className="text-base text-primary-main font-bold">
                  {formatPickDate(currentDate)}
                </span>
                <span className="text-sm text-kin-text font-light">
                  {isFetching ? 'Updating...' : `${classPackage.length} Classes`}
                </span>
              </div>

              {isLoading && (
                <div className="flex flex-col gap-4">
                  {[1, 2, 3, 4].map((n) => (
                    <SkeletonCard key={n} />
                  ))}
                </div>
              )}

              {!isLoading && classPackage.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-center gap-2">
                  <div className="text-lg font-semibold text-kin-text">No Classes Available</div>
                  <p className="text-sm text-gray-500 max-w-xs">
                    Try choosing a different date or filter to see other available class schedules.
                  </p>
                </div>
              )}

              {/* List */}
              {!isLoading && classPackage.length > 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.25 }}
                  className="flex flex-col gap-4 text-kin-text">
                  {sortByStartTime(classPackage).map((items) => (
                    <CardClassSchedule key={items.id} item={items} onBooking={onBooking} />
                  ))}
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Booking */}
        {openBooking && (
          <ModalBooking
            schedule={selectedSchedule}
            openModal={openBooking}
            setOpenModal={setOpenBooking}
          />
        )}
      </div>
    </FullLayout>
  );
};

export default ClassSchedule;
